import React, { useEffect, useMemo, useState } from 'react';
import classname from 'classnames';
import {
    ICmsNavItemsData,
    ICusNewNavMenuProps,
    IImageLinksData,
    IPromotedLinksData,
    ISubMenusData
} from './cus-new-nav-menu.props.autogenerated';
import { ICusNewNavMenuData, ISubMenusDataWithGrouping } from './cus-new-nav-menu.data';
import { GenderCookieValue, GENDER_COOKIE_NAME } from '../ext-header/components';
import { getCookieValueFromContext } from '../../shared/functions/cookie';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { useIsClientSide } from '../../utils/hooks';
import { IGridSettings, Image } from '@msdyn365-commerce/core-internal';
import { BaseImageSettings, useImageSettings } from '../../shared/hooks/useImageSettings';

const BASE_IMAGE_VIEWPORT_SETTINGS: BaseImageSettings = {
    xs: { w: 256, h: 383 },
    sm: { w: 330, h: 495 },
    md: { w: 190, h: 285 },
    lg: { w: 190, h: 285 },
    xl: { w: 190, h: 285 }
};

const getSelectedMenuItem = (menuItemsData: ICmsNavItemsData[], activeTab: string | undefined, isMobile: boolean) => {
    // Always set the first existing tab when mobile as fallback value
    if (!activeTab && isMobile) {
        return menuItemsData[0];
    }
    return menuItemsData.find(item => item.labelText === activeTab);
};

const getLinkTarget = (openInNewTab: boolean | undefined) => {
    return openInNewTab ? '_blank' : '_self';
};

const MenuItems = ({ items, onDrill }: { items: ISubMenusDataWithGrouping[]; onDrill: (item: ISubMenusDataWithGrouping) => void }) => {
    return (
        <ul className='new-nav-menu__menu-items-container'>
            {items.map(item => {
                const hasChildren = !!item.subMenus?.length;
                return (
                    <li
                        key={item.labelText}
                        className={classname('new-nav-menu__menu-item', {
                            'new-nav-menu__menu-item--last-in-group': item.isLastInGroup,
                            'new-nav-menu__menu-item--highlighted': item.isHighlighted
                        })}
                    >
                        {hasChildren && (
                            <button type='button' onClick={() => onDrill(item)} aria-label={item.ariaLabel || item.labelText}>
                                {item.labelText}
                            </button>
                        )}
                        {!hasChildren && item.linkUrl && (
                            <a href={item.linkUrl.destinationUrl} aria-label={item.ariaLabel || item.labelText}>
                                {item.labelText}
                            </a>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

const RootLevelLinks = ({ items, type }: { items: IPromotedLinksData[] | undefined; type: 'promotional' | 'informational' }) => {
    if (!items || items.length === 0) {
        return <></>;
    }
    return (
        <ul
            className={classname('new-nav-menu__root-level-links-container', {
                'new-nav-menu__root-level-links-container--position-top': type === 'promotional',
                'new-nav-menu__root-level-links-container--position-bottom': type === 'informational'
            })}
        >
            {items.map(item => (
                <li key={item.labelText}>
                    <a
                        className={classname('new-nav-menu__root-level-link', {
                            'new-nav-menu__root-level-link--large': type === 'promotional'
                        })}
                        href={item.linkUrl.destinationUrl}
                        target={getLinkTarget(item.openInNewTab)}
                        aria-label={item.ariaLabel || item.labelText}
                    >
                        {item.labelText}
                    </a>
                </li>
            ))}
        </ul>
    );
};

const ImageLinks = ({
    imageLinks,
    gridSettings
}: {
    imageLinks: IImageLinksData[] | undefined;
    gridSettings: IGridSettings | undefined;
}) => {
    if (!imageLinks || imageLinks.length === 0) {
        return <></>;
    }

    const imageViewportSettings = useImageSettings(BASE_IMAGE_VIEWPORT_SETTINGS);

    return (
        <div className='new-nav-menu__image-links-container'>
            {imageLinks.map(imageLink => (
                <a
                    key={imageLink.labelTitle}
                    href={imageLink.linkUrl.destinationUrl}
                    target={getLinkTarget(imageLink.openInNewTab)}
                    className='new-nav-menu__image-link'
                    aria-label={imageLink.ariaLabel || imageLink.labelTitle}
                >
                    <Image
                        className='new-nav-menu__image'
                        src={imageLink.image?.src}
                        alt={imageLink.image?.altText}
                        title={imageLink.image?.title}
                        imageSettings={{ ...imageLink.image?.imageSettings, viewports: imageViewportSettings }}
                        gridSettings={gridSettings ?? {}}
                    />
                    {imageLink.labelTitle && (
                        <span className='new-nav-menu__image-link-label'>
                            {imageLink.labelTitle}
                            {imageLink.labelSubtitle && (
                                <span className='new-nav-menu__image-link-label-subtitle'>{imageLink.labelSubtitle}</span>
                            )}
                        </span>
                    )}
                </a>
            ))}
        </div>
    );
};

const NavigationPanel = ({
    item,
    isMobile = false,
    stack,
    gridSettings,
    handleBackButtonClick,
    handleMenuItemClick
}: {
    item: ISubMenusData | ICmsNavItemsData;
    isMobile?: boolean;
    stack: ISubMenusData[];
    gridSettings: IGridSettings | undefined;
    handleBackButtonClick: () => void;
    handleMenuItemClick: (item: ISubMenusDataWithGrouping) => void;
}) => {
    const currentItems = useMemo(() => {
        if (stack.length) {
            return stack[stack.length - 1].subMenus ?? [];
        }
        if ('subMenuGroups' in item && item.subMenuGroups) {
            return item.subMenuGroups.flatMap(group =>
                group.subMenus.map((subMenu, i) => {
                    return { ...subMenu, isLastInGroup: i === group.subMenus.length - 1 };
                })
            );
        }
        if ('subMenus' in item) {
            return item.subMenus ?? [];
        }
        return [];
    }, [stack, item]);

    const title = stack.length ? stack[stack.length - 1].labelText : undefined;
    const imageLinks = stack.length ? stack[stack.length - 1].imageLinks : item.imageLinks;
    const shouldRenderHeader = title || stack.length > 0;

    return (
        <>
            <div className={classname('new-nav-menu__panel-main-container')}>
                {shouldRenderHeader && (
                    <div className='new-nav-menu__panel-header'>
                        {stack.length > 0 && (
                            <button type='button' className='new-nav-menu__back' onClick={handleBackButtonClick}>
                                ←
                            </button>
                        )}
                        {title && <div className='new-nav-menu__title'>{title}</div>}
                    </div>
                )}
                {!stack.length && 'promotedLinks' in item && <RootLevelLinks items={item.promotedLinks} type='promotional' />}
                <MenuItems items={currentItems} onDrill={item => handleMenuItemClick(item)} />
                {isMobile && <ImageLinks imageLinks={imageLinks} gridSettings={gridSettings} />}
                {'informationalLinks' in item && <RootLevelLinks items={item.informationalLinks} type='informational' />}
            </div>
            {!isMobile && <ImageLinks imageLinks={imageLinks} gridSettings={gridSettings} />}
        </>
    );
};

const NewNavMenu = (props: ICusNewNavMenuProps<ICusNewNavMenuData>) => {
    const {
        config: { cmsNavItems },
        context
    } = props;

    if (!cmsNavItems) {
        return <></>;
    }

    const isMobile = useIsMobile(context);
    const isClientSide = useIsClientSide();
    // Since we cannot know the state of whether the panel has been opened in mobile
    // we need to default to first item and track if the user has interacted in desktop
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
    const [hasInteractedDesktop, setHasInteractedDesktop] = useState(false);
    const [stack, setStack] = useState<ISubMenusData[]>([]);
    const [hasSetDefaultGender, setHasSetDefaultGender] = useState(false);

    useEffect(() => {
        if (!isMobile || hasSetDefaultGender) {
            return;
        }
        const genderCookieValue = getCookieValueFromContext<GenderCookieValue>(GENDER_COOKIE_NAME, context);
        if (genderCookieValue === 'male' && cmsNavItems) {
            const defaultMaleCategory = cmsNavItems.find(({ isDefaultMaleCategory }) => !!isDefaultMaleCategory);
            if (defaultMaleCategory) {
                setActiveTab(defaultMaleCategory.labelText);
                setHasSetDefaultGender(true);
            }
        }
    }, [isMobile]);

    const selectedMenuItem = useMemo(() => getSelectedMenuItem(cmsNavItems, activeTab, isMobile), [cmsNavItems, activeTab, isMobile]);

    const handleTogglePanel = (item: ICmsNavItemsData | undefined, isMobileEvent = false) => () => {
        const tabLabel = item?.labelText;
        if ((!isMobileEvent && isMobile) || tabLabel === activeTab) {
            return;
        }
        const hasSubMenus = !!item?.subMenuGroups?.length;
        setActiveTab(hasSubMenus ? tabLabel : undefined);
        setStack([]);
        if (!hasInteractedDesktop) {
            setHasInteractedDesktop(true);
        }
    };

    const handleBackButtonClick = () => {
        setStack(s => s.slice(0, -1));
    };

    const handleMenuItemClick = (item: ISubMenusDataWithGrouping) => {
        setStack(s => [...s, item]);
    };

    const getMainCategoryButtonCommonProps = (item: ICmsNavItemsData) => {
        const isMobileFallbackValue = isMobile && !activeTab && item.labelText === cmsNavItems?.[0].labelText;
        return {
            className: classname('new-nav-menu__category-button', {
                'new-nav-menu__category-button--selected': activeTab === item.labelText || isMobileFallbackValue
            }),
            'aria-label': item.ariaLabel || item.labelText
        };
    };

    const renderNavigationPanel = () => {
        if (!selectedMenuItem || !isClientSide) {
            return <></>;
        }
        return (
            <NavigationPanel
                item={selectedMenuItem}
                stack={stack}
                gridSettings={context.request.gridSettings}
                handleBackButtonClick={handleBackButtonClick}
                handleMenuItemClick={handleMenuItemClick}
                isMobile={isMobile}
            />
        );
    };

    return (
        <>
            <nav className='new-nav-menu' onMouseLeave={handleTogglePanel(undefined)}>
                <ul className='new-nav-menu__main-categories'>
                    {cmsNavItems.map(item => {
                        const itemButtonCommonProps = getMainCategoryButtonCommonProps(item);
                        return (
                            <li key={item.labelText} onMouseEnter={handleTogglePanel(item)}>
                                {isMobile && !!item.subMenuGroups?.length ? (
                                    <button type='button' onClick={handleTogglePanel(item, true)} {...itemButtonCommonProps}>
                                        {item.labelText}
                                    </button>
                                ) : (
                                    <a href={item.linkUrl.destinationUrl} {...itemButtonCommonProps}>
                                        {item.labelText}
                                    </a>
                                )}
                            </li>
                        );
                    })}
                    {!isMobile && (
                        <div
                            aria-hidden={!activeTab || !hasInteractedDesktop}
                            className={classname('new-nav-menu__panel', {
                                'new-nav-menu__panel--visible': !!activeTab && hasInteractedDesktop
                            })}
                        >
                            {renderNavigationPanel()}
                        </div>
                    )}
                </ul>
                {isMobile && renderNavigationPanel()}
            </nav>
            {!isMobile && (
                <div
                    aria-hidden={!activeTab}
                    className={classname('new-nav-menu-overlay', {
                        'new-nav-menu-overlay--visible': !!activeTab && hasInteractedDesktop
                    })}
                ></div>
            )}
        </>
    );
};

export default NewNavMenu;
